import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { eQMSScopeConstants } from '../eqms-scope-constants';
import { EqmsScopeService } from '../eqms-scope.service';
import { EqmsScopeManagementService } from '../../shared/services/eqms-scope-management.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { TreeNode } from 'primeng/api';
import { ScopeOperationType } from '../models/export';
import { ScopeCategoryDto } from '../models/scope-category.dto';
import { delay, forkJoin, of, finalize, switchMap } from 'rxjs';
import { CreateScopeDto } from '../models/create-scope-dto';
import { SpinnerService } from '../../shared/services/spinner.service';
import { cloneDeep } from 'lodash-es';

@Component({
  selector: 'app-scope-form-slider',
  templateUrl: './scope-form-slider.component.html',
  styleUrls: ['./scope-form-slider.component.scss']
})
export class ScopeFormSliderComponent implements OnChanges {
  @Output() iseQMSScopeSliderActive = new EventEmitter<boolean>();
  @Input() iseQMSScopeSliderOpen: boolean = false;
  @Input() isGlobalEqmsInstance: boolean = true;
  @Input() isOnlyScopeManager: boolean = false;
  @Input() selectedScope: any = null;
  @Input() treeData: TreeNode[] = [];
  eQMSScopeSliderTitle: String = eQMSScopeConstants.eQMSScopeManagementTitle;
  iseQMSScopeUpdated: boolean = false;
  isSubmitting: boolean = false;
  isLoadingDropdowns: boolean = false;
  isScopeUpdated: boolean = false;
  eqmsScopeForm!: FormGroup;
  parentEqmsScopeOptions: any[] = [];
  sourceItaagOptions: any[] = [];
  scopeCategoryOptions: any[] = [];
  parentTreeData: TreeNode[] = [];
  private scopeCategoryData: ScopeCategoryDto[] = [];
  existingCategoryName: string | null = null;
  valueField: string = 'value';
  labelField: string = 'label';
  parentScope: TreeNode | null = null;
  @Input() primaryScopeId: number = 0; 

  infoForSourceItaag: string = eQMSScopeConstants.infoForSourceItaag;
  infoForForbidChanges: string = eQMSScopeConstants.infoForForbidChanges;

  // [hierarchy field, ScopeRequestDto field] pairs for every field the update endpoint accepts;
  // drives pollUntilScopeUpdated's "did this edit apply yet?" check so new fields can't be missed.
  private static readonly SCOPE_UPDATE_FIELD_MAP: ReadonlyArray<[string, string]> = [
    ['eQMSScopeName', 'ScopeName'],
    ['scopeCategoryID', 'ScopeCategory'],
    ['sourceItaag', 'SourceItaag'],
    ['canAssignDocuments', 'CanAssignDocuments'],
    ['canAssignTrainings', 'CanAssignTrainings'],
    ['canAssignChangeControl', 'CanAssignChangeControl'],
    ['forbidChangesForInReviewDocument', 'ForbidChangesAfterDocumentFormattingPhase'],
    ['printLockDocuments', 'PrintLockDocuments']
  ];

  get isEditMode(): boolean {
    return this.eqmsScopeService.isEditEQMSScopeSlider;
  }

  get isGlobalInstance(): boolean {
    return this.isGlobalEqmsInstance;
  }

  get isScopeManagerOnly(): boolean {
    return this.isOnlyScopeManager;
  }

  constructor(private eqmsScopeService: EqmsScopeService, private formBuilder: FormBuilder,
     private messageService: MessageService, private eqmsScopeManagementService: EqmsScopeManagementService,
    private readonly spinnerService: SpinnerService) {

  }
  ngOnInit(): void {
    this.eQMSScopeSliderTitle = this.eqmsScopeService.isEditEQMSScopeSlider ?
      eQMSScopeConstants.eQMSScopeManagementTitle_For_Edit : eQMSScopeConstants.eQMSScopeManagementTitle_For_Create;
    this.eqmsScopeForm = this.createFormGroup();
    this.eqmsScopeService.closeAndResetEQMSScopeSlider$.subscribe((isClose) => {
      if (isClose) { 
        this.closeAndResetEQMSScopeSlider();
        // Reset form when slider closes
        this.resetForm();
      }
    });
    // Populate form immediately if we have selected scope data and in edit mode
    if (this.selectedScope && this.isEditMode) {
      this.populateFormWithScopeData();
    }
  }

  /**
   * Extracts parent scope and all its descendants as a new tree
   * @param nodes - Complete tree nodes array
   * @param parentScopeId - ID of the parent scope to extract
   * @returns Array containing the parent node with its complete descendant tree
   */
  private extractScopeSubtree(nodes: TreeNode[], parentScopeId: number): TreeNode[] {
    // Find the target parent node
    const parentNode = this.findNodeById(nodes, parentScopeId);

    if (!parentNode) {
      return []; // Parent not found
    }

    // Return the parent node with all its descendants
    return [parentNode];
  }

  /**
   * Recursively finds a node by scope ID
   */
  private findNodeById(nodes: TreeNode[], scopeId: number): TreeNode | null {
    for (const node of nodes) {
      // Check if current node matches
      if (node.data?.eQMSScopeId === scopeId) {
        return node; // Return complete node with all descendants
      }

      // Recursively search in children
      if (node.children) {
        const found = this.findNodeById(node.children, scopeId);
        if (found) return found;
      }
    }
    return null;
  }

  

  private loadDropdownOptions(): void {
    this.isLoadingDropdowns = true;
    const operationType = this.isEditMode ? ScopeOperationType.UpdateScope : ScopeOperationType.CreateScope;
    // Make parallel API calls using forkJoin
    forkJoin([
      this.eqmsScopeService.getGlobalItaags(operationType),
      this.eqmsScopeService.getScopeCategories(),
    ]).subscribe({
      next: ([itaagData, categoryData]) => {
        // Store raw category data for form population in edit mode
        this.scopeCategoryData = categoryData;

      // Transform ITAAG NameCode data: use code for both value and label
        this.sourceItaagOptions = itaagData.map(item => ({
          value: item.code,
          label: item.code
        }));
       
        if (this.primaryScopeId === 1 || !this.isOnlyScopeManager) {
          this.parentTreeData = cloneDeep(this.treeData); // Show entire tree if primary scope ID is 1
        }
        else if (this.primaryScopeId > 1) {
         const subtree = this.extractScopeSubtree(this.treeData, this.primaryScopeId);
         this.parentTreeData = cloneDeep(subtree);
        }
        else {
          this.parentTreeData = []; // No parent options if primary scope ID is invalid
        }

        // Transform and filter ScopeCategory DTO data for dropdown display:
        // - Filter only objects where scopeCategoryName === scopeCategoryType
        // - Create { value: scopeCategoryID, label: scopeCategoryName }
        this.scopeCategoryOptions = categoryData
          .filter(item => item.scopeCategoryName === item.scopeCategoryType)
          .map(item => ({
            value: item.scopeCategoryID,
            label: item.scopeCategoryName
          }));

        // If in edit mode with a selected scope, check if we need to add the existing non-matching category
        if (this.isEditMode && this.selectedScope && this.selectedScope.scopeCategoryID) {
          const existingCategory = categoryData.find(cat => cat.scopeCategoryID === this.selectedScope.scopeCategoryID);
          if (existingCategory) {
            this.existingCategoryName = existingCategory.scopeCategoryName;
            // Check if this category is in the filtered dropdown options
            const isInFilteredOptions = this.scopeCategoryOptions.some(opt => opt.value === this.selectedScope.scopeCategoryID);
            // If not, add it as disabled (read-only) so existing data displays properly
            if (!isInFilteredOptions) {
              this.scopeCategoryOptions = [
                {
                  value: existingCategory.scopeCategoryID,
                  label: existingCategory.scopeCategoryName,
                  disabled: true
                },
                ...this.scopeCategoryOptions
              ];
            }
          }
        }

        this.isLoadingDropdowns = false;
      },
      error: (error) => {
        this.isLoadingDropdowns = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load dropdown options. Please try again.'
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset form when slider is closed
    if (changes['iseQMSScopeSliderOpen'] && !this.iseQMSScopeSliderOpen) {
      this.resetForm();
      return;
    }

    // Load dropdown data when slider opens
    if (changes['iseQMSScopeSliderOpen'] && this.iseQMSScopeSliderOpen) {
      this.loadDropdownOptions();
    }

    // Populate form when selectedScope changes and slider is open
    if (changes['selectedScope'] && this.selectedScope && this.isEditMode && this.eqmsScopeForm && this.iseQMSScopeSliderOpen) {
      this.populateFormWithScopeData();
    }
  }

  private populateFormWithScopeData(): void {
    if (!this.selectedScope || !this.eqmsScopeForm) {
      return;
    }

    const formData = {
      name: this.selectedScope.eQMSScopeName,
      scopeCategory: this.selectedScope.scopeCategoryID,
      parentEqmsScope: this.selectedScope.parentEQMSScopeId,
      sourceItaag: this.selectedScope.sourceItaag,
      acceptDocuments: this.selectedScope.canAssignDocuments,
      acceptCourses: this.selectedScope.canAssignTrainings,
      acceptChangeControls: this.selectedScope.canAssignChangeControl,
      forbidChangesAfterFormattingPhase: this.selectedScope.forbidChangesForInReviewDocument,
      isDocumentPrintingBlocked: this.selectedScope.printLockDocuments
    };

    this.eqmsScopeForm.patchValue(formData);
  }

  createFormGroup(): FormGroup {
    return this.formBuilder.group({
      name: new FormControl(null, [Validators.required]),
      scopeCategory: new FormControl(null, [Validators.required]),
      parentEqmsScope: !this.isEditMode?new FormControl(null, [Validators.required]):new FormControl(null),
      sourceItaag: new FormControl(null),
      acceptDocuments: new FormControl(false),
      acceptCourses: new FormControl(false),
      acceptChangeControls: new FormControl(false),
      forbidChangesAfterFormattingPhase: new FormControl(false),
      isDocumentPrintingBlocked: new FormControl(false)
    });
  }

  onParentScopeSelect(event: any)
  {
  if (event && event.node && event.node.data) {
    const selectedNodeData = event.node.data;
    this.eqmsScopeForm.setControl('parentEqmsScope', new FormControl(selectedNodeData ? selectedNodeData.eQMSScopeId : null, [Validators.required]));
    }
    else{
      this.eqmsScopeForm.setControl('parentEqmsScope', new FormControl(null));
    }
  }

  closeSlider() {
    this.eqmsScopeService.openConfirmationDialog();
  }

  private resetForm(): void {
    if (this.eqmsScopeForm) {
      this.eqmsScopeForm.reset();
      this.eqmsScopeForm.markAsUntouched();
      this.eqmsScopeForm.markAsPristine();
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.eqmsScopeForm.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  submitScopeForm(): void {
    if (!this.eqmsScopeForm.valid) {
      return;
    }

    if (this.isEditMode) {
      this.submitUpdateScope();
    }
    else{
      this.submitCreateScope();
    }
  }

  private validateScopeNameUniqueness(): boolean {
    const newScopeName = this.eqmsScopeForm.get('name')?.value;
    
    if (!newScopeName || !this.selectedScope) {
      return true;
    }

    // Get sibling IDs from the current selected scope
    const siblingIds = this.selectedScope.sibilings || [];
    
    // Get all sibling scope objects from the tree
    const siblingScopes = this.findScopesByIds(siblingIds);
    
    // Check if any sibling has the same name (case-insensitive)
    const isDuplicateName = siblingScopes.some((sibling: any) => 
      sibling.eQMSScopeName.trim().toLowerCase() === newScopeName.trim().toLowerCase()
    );
    
    if (isDuplicateName) {
      this.messageService.add({
        severity: 'error',
        summary: eQMSScopeConstants.Scope_Slider_Duplicate_Scope_Name_Error,
      });
      return false;
    }
    
    return true;
  }

  private findScopesByIds(scopeIds: number[]): any[] {
    const foundScopes: any[] = [];
    
    // Recursively search through the tree to find scopes with matching IDs
    const searchTree = (nodes: TreeNode[] | undefined) => {
      if (!nodes) {
        return;
      }
      
      nodes.forEach((node: TreeNode) => {
        if (node.data && scopeIds.includes(node.data.eQMSScopeId)) {
          foundScopes.push(node.data);
        }
        // Recursively search in children
        if (node.children) {
          searchTree(node.children);
        }
      });
    };
    
    searchTree(this.treeData);
    return foundScopes;
  }

  private submitUpdateScope(): void {
    // Validate scope name uniqueness before submitting
    if (!this.validateScopeNameUniqueness()) {
      return;
    }
    
    if (!this.selectedScope) {
      this.messageService.add({
        severity: eQMSScopeConstants.Severity_Err,
        summary: eQMSScopeConstants.Scope_Slider_No_Scope_Selected_Error_Message,
      });
      return;
    }

    this.isSubmitting = true;
    const scopeId = this.selectedScope.eQMSScopeId;
    const scopeData = this.mapFormToScopeRequestDto();

    this.spinnerService.show();
    this.eqmsScopeService.updateScope(scopeId, scopeData).pipe(
      switchMap(() => this.eqmsScopeManagementService.pollUntilScopeUpdated(
        scopeId,
        (scope) => ScopeFormSliderComponent.SCOPE_UPDATE_FIELD_MAP.every(
          ([hierarchyField, dtoField]) => (scope as any)[hierarchyField] === scopeData[dtoField]
        )
      )),
      finalize(() => this.spinnerService.hide())
    ).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: eQMSScopeConstants.Severity_Success,
          summary: eQMSScopeConstants.Scope_Slider_Update_Success_Message
        });
        // Cache is already cleared/refreshed by pollUntilScopeUpdated; just notify listeners
        this.eqmsScopeManagementService.scopeUpdated$.next(true);
        // Close the slider
        this.iseQMSScopeUpdated = true;
        this.eqmsScopeService.closeAndResetEQMSScopeSlider$.next(true);
      },
      error: (error) => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: eQMSScopeConstants.Severity_Err,
          summary: eQMSScopeConstants.Scope_Slider_Update_Error_Message,
        });
      }
    });
  }

  private mapFormToScopeRequestDto(): any {
    return {
      ScopeName: this.eqmsScopeForm.get('name')?.value,
      ScopeCategory: this.eqmsScopeForm.get('scopeCategory')?.value,
      SourceItaag: this.eqmsScopeForm.get('sourceItaag')?.value,
      CanAssignDocuments: this.eqmsScopeForm.get('acceptDocuments')?.value,
      CanAssignTrainings: this.eqmsScopeForm.get('acceptCourses')?.value,
      CanAssignChangeControl: this.eqmsScopeForm.get('acceptChangeControls')?.value,
      ForbidChangesAfterDocumentFormattingPhase: this.eqmsScopeForm.get('forbidChangesAfterFormattingPhase')?.value,
      PrintLockDocuments: this.eqmsScopeForm.get('isDocumentPrintingBlocked')?.value
    };
  }

  private submitCreateScope(): void {
    if (!this.validateScopeNameUniquenessforCreate()) {
      return;
    }
    this.spinnerService.show();
    this.eqmsScopeService.createScope(this.mapFormToCreateScopeDto()).pipe(delay(5000), finalize(() => this.spinnerService.hide())).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: eQMSScopeConstants.Severity_Success,
          summary: eQMSScopeConstants.Scope_Slider_Create_Success_Message,
        });

        this.eqmsScopeManagementService.clearTreeData();
        this.eqmsScopeManagementService.scopeUpdated$.next(true);
        // Close the slider       
        this.iseQMSScopeUpdated = true;
        this.eqmsScopeService.closeAndResetEQMSScopeSlider$.next(true);
      },
      error: (error) => {
        const errorMessage = error?.error?.detail || eQMSScopeConstants.Scope_Slider_Create_Error_Message;
        this.messageService.add({
          severity: eQMSScopeConstants.Severity_Err,
          summary: eQMSScopeConstants.Scope_Slider_Create_Error_Message
        });
      }
    });
  }

  validateScopeNameUniquenessforCreate(): boolean {
    const newScopeName = this.eqmsScopeForm.get('name')?.value;
    const parentScopeId = this.eqmsScopeForm.get('parentEqmsScope')?.value;
    
    if (!newScopeName || parentScopeId === null || parentScopeId === undefined) {
      return true;
    }
    const scope = this.findScopesByIds([Number(parentScopeId)]);
    const childScopes = scope.length > 0 ? scope[0].children || [] : [];   
    const isDuplicateName = childScopes.some((child: any) => 
      child.eQMSScopeName.trim().toLowerCase() === newScopeName.trim().toLowerCase()
    );
    if (isDuplicateName) {
      this.messageService.add({
        severity: eQMSScopeConstants.Severity_Err,
        summary: eQMSScopeConstants.Scope_Slider_Duplicate_Scope_Name_Error,
      });
      return false;
    }
    return true;

  }

  mapFormToCreateScopeDto(): CreateScopeDto {
    return {
      parentEQMSScopeId: this.eqmsScopeForm.get('parentEqmsScope')?.value,
      scopeName: this.eqmsScopeForm.get('name')?.value,
      sourceItaag: this.eqmsScopeForm.get('sourceItaag')?.value,
      scopeCategory: this.eqmsScopeForm.get('scopeCategory')?.value,
      canAssignDocuments: this.eqmsScopeForm.get('acceptDocuments')?.value,
      canAssignTrainings: this.eqmsScopeForm.get('acceptCourses')?.value,
      canAssignChangeControl: this.eqmsScopeForm.get('acceptChangeControls')?.value,
      forbidChangesAfterDocumentFormattingPhase: this.eqmsScopeForm.get('forbidChangesAfterFormattingPhase')?.value,
      printLockDocuments: this.eqmsScopeForm.get('isDocumentPrintingBlocked')?.value
    };
  }

  closeAndResetEQMSScopeSlider() {
    this.iseQMSScopeSliderOpen = false;
    this.iseQMSScopeSliderActive.emit(this.iseQMSScopeUpdated);
  }

}
